import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, FileText, Play, User, Clock, TrendingUp, X } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const RECENTS_KEY = "luminon_recent_searches";
const TRENDING = [
  "Light — Reflection and Refraction",
  "Trigonometry",
  "Newton's Laws",
  "Chemical Bonding",
  "Photosynthesis",
  "Electric Charges",
];

function loadRecents() {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
  } catch { return []; }
}
function saveRecent(q) {
  if (!q || q.length < 2) return;
  const cur = loadRecents().filter((r) => r.toLowerCase() !== q.toLowerCase());
  cur.unshift(q);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(cur.slice(0, 6)));
}

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ notes: [], videos: [], teachers: [] });
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (open) { setRecents(loadRecents()); setQ(""); } }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.length < 2) { setRes({ notes: [], videos: [], teachers: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setRes(r.data);
      } catch (err) {
        console.error("search failed", err);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (path, term) => {
    if (term) saveRecent(term);
    onClose();
    navigate(path);
  };

  const clearRecents = (e) => {
    e.stopPropagation();
    localStorage.removeItem(RECENTS_KEY);
    setRecents([]);
  };

  const autocomplete = useMemo(() => {
    if (q.length < 1) return [];
    const pool = [...res.notes, ...res.videos];
    return pool.slice(0, 4).map((it) => it.title);
  }, [q, res]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-3xl border-border" data-testid="global-search">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <Search className="w-5 h-5 text-muted-foreground" strokeWidth={1.8} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search notes, videos, chapters, topics..."
            data-testid="global-search-input"
            className="flex-1 bg-transparent outline-none text-lg font-medium"
            list="search-autocomplete"
          />
          {loading && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border">ESC</kbd>
        </div>
        <datalist id="search-autocomplete">
          {autocomplete.map((s) => <option key={s} value={s} />)}
        </datalist>

        <div className="max-h-96 overflow-y-auto p-3">
          {q.length < 2 ? (
            <div className="space-y-4">
              {recents.length > 0 && (
                <div>
                  <div className="px-3 py-1 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> Recent
                    </span>
                    <button onClick={clearRecents} data-testid="clear-recents"
                      className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recents.map((r) => (
                      <button key={r} onClick={() => setQ(r)} data-testid={`recent-${r}`}
                        className="w-full text-left flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate">{r}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" /> Trending
                </div>
                <div className="flex flex-wrap gap-2 px-3 pt-1">
                  {TRENDING.map((t) => (
                    <button key={t} onClick={() => setQ(t)} data-testid={`trending-${t}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors">
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <Section title="Notes" items={res.notes} render={(n) => (
                <button key={n.note_id} onClick={() => go(`/browse/notes?class=${n.class_level}&subject=${encodeURIComponent(n.subject)}`, q)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-muted" data-testid={`search-note-${n.note_id}`}>
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground truncate">Class {n.class_level} · {n.subject} · {n.chapter}</div>
                  </div>
                </button>
              )} />
              <Section title="Videos" items={res.videos} render={(v) => (
                <button key={v.video_id} onClick={() => go(`/browse/learnings?class=${v.class_level}&subject=${encodeURIComponent(v.subject)}`, q)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-muted" data-testid={`search-video-${v.video_id}`}>
                  <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                    <Play className="w-4 h-4 text-secondary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{v.title}</div>
                    <div className="text-xs text-muted-foreground truncate">Class {v.class_level} · {v.subject}</div>
                  </div>
                </button>
              )} />
              {res.notes.length + res.videos.length === 0 && q.length >= 2 && !loading && (
                <div className="text-center py-10 text-sm text-muted-foreground">No matches for "{q}"</div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, items, render }) {
  if (!items?.length) return null;
  return (
    <div className="mb-3">
      <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</div>
      <div className="space-y-1">{items.slice(0, 5).map(render)}</div>
    </div>
  );
}
